<template>
  <v-container fluid>
    <HeaderBar>
      <v-icon slot="icon">event</v-icon>
      Event
    </HeaderBar>

    <v-layout v-if="loading">
      <v-progress-linear indeterminate />
    </v-layout>

    <v-container v-else-if="error">
      <v-alert :value="true" type="error">Couldn't find that event!</v-alert>
    </v-container>

    <v-container v-else>
      <v-layout column>
        <v-card>
          <v-card-title>
            <h2>{{ event.title }}</h2>
          </v-card-title>

          <v-card-text>
            <v-flex class="mb-2">
              <v-layout row align-baseline>
                <v-flex xs2>
                  <v-chip label outline color="blue">WHEN</v-chip>
                </v-flex>
                <v-flex xs10>
                  <p>{{ event.start | moment("dddd MMMM DD YYYY, h:mma") }} - {{ event.end | moment("h:mma") }} </p>
                </v-flex>
              </v-layout>
            </v-flex>

            <v-flex v-if="event.location" class="mb-2">
              <v-layout row align-baseline>
                <v-flex xs2>
                  <v-chip label outline color="blue">LOCATION</v-chip>
                </v-flex>
                <v-flex xs10>
                  <p>{{ event.location }}</p>
                </v-flex>
              </v-layout>
            </v-flex>

            <v-flex class="mb-2">
              <v-layout row align-baseline>
                <v-flex xs2>
                  <v-chip label outline color="blue">DESCRIPTION</v-chip>
                </v-flex>
                <v-flex xs10>
                  <div
                    style="white-space: pre-wrap"
                    v-html="event.description">
                  </div>
                </v-flex>
              </v-layout>
            </v-flex>

            <v-flex class="mb-2" v-if="event.clockable">
              <v-layout row align-baseline>
                <v-flex xs2>
                  <v-chip label outline color="blue">CLOCK IN REQUIRED</v-chip>
                </v-flex>
                <v-flex xs10>
                  <p>You will need to clock in at the beginning of this event and clock out at the end</p>
                </v-flex>
              </v-layout>
            </v-flex>

            <v-layout class="mt-2" column v-if="user.role === 'employee'">
              <v-divider class="my-2" />

              <v-flex>
                <rsvpAction :eventId="eventId" />
              </v-flex>

              <v-flex v-if="event.clockable">
                <clockInAction :eventId="eventId" />
              </v-flex>

            </v-layout>
          </v-card-text>

          <v-card-actions v-if="user.managerOrHigher">
            <v-btn flat color="primary" :to="{ name: 'eventEdit', params: { eventId } }">
              <v-icon left>edit</v-icon> Edit
            </v-btn>
            <v-btn flat color="error" @click="openDeleteDialog(eventId)">
              <v-icon left>delete</v-icon> Delete
            </v-btn>
            <v-btn flat color="primary" v-if="hasGuests" @click="exportGuestList">
              <v-icon left>file_download</v-icon> Download Guest List
            </v-btn>
          </v-card-actions>

        </v-card>
      </v-layout>

      <v-dialog v-model="deleteDialogOpen" max-width="480" persistent>
        <v-card>
          <v-card-title class="headline">Delete this event?</v-card-title>
          <v-card-text>
            <p>RSVPs will be removed.</p>
            <v-alert v-if="deleteDialogError" :value="true" type="error">
              {{ deleteDialogError }}
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn flat @click="closeDeleteDialog" :disabled="deleting">Cancel</v-btn>
            <v-btn color="error" :loading="deleting" @click="handleConfirmDelete">Delete</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

    </v-container>

  </v-container>
</template>

<script>
import eventApi from '@/api/event'
import rsvpAction from '@/components/event/rsvp-action.vue';
import clockInAction from '@/components/event/clock-in-action.vue';
import eventActionsMixin from '@/components/event/event-actions-mixin';
import { guestListRows } from '@/components/event/guest-list-csv';
import { ExportToCsv } from 'export-to-csv';
import { mapState } from 'vuex';

export default {
  components: {
    rsvpAction, clockInAction
  },
  mixins: [eventActionsMixin],
  created () {
    this.eventId = this.$route.params.eventId
    this.fetchEvent()
  },
  data: function () {
    return {
      loading: false,
      rsvpLoading: false,
      rsvp: {},
      error: false,
      event: {},
      eventId: null,
    }
  },
  computed: {
    ...mapState("userAuth", ["user"]),
    hasGuests() {
      return Array.isArray(this.event.event_rsvps) && this.event.event_rsvps.length > 0
    },
  },
  methods: {
    fetchEvent() {
      this.loading = true
      return eventApi.get(this.eventId)
      .then((r) => {
        this.event = r.data
        return r.data.id
      })
      .catch((e) => {
        this.error = true
      })
      .finally(() => { this.loading = false })
    },
    exportGuestList() {
      const options = {
        fieldSeparator: ",",
        quoteStrings: '"',
        decimalSeparator: ".",
        showLabels: true,
        showTitle: false,
        filename: `guest_list_${(this.event.title || 'event').replace(/[^a-z0-9]+/gi, '_').toLowerCase()}_${this.$moment().format("YYYY-MM-DD")}`,
        useTextFile: false,
        useBom: true,
        useKeysAsHeaders: true,
      }
      const rows = guestListRows(this.event, (iso) => {
        const m = this.$moment(iso)
        return m.isValid() ? m.format("MMM D, YYYY h:mma") : ''
      })
      new ExportToCsv(options).generateCsv(rows)
    },
    handleConfirmDelete() {
      this.confirmDelete().then((result) => {
        if (result.deleted) {
          this.$store.dispatch('global/showSnackbarSuccess', { message: 'Event cancelled' })
          this.$router.push({ name: 'eventIndex' })
        }
      })
    },
  },
}
</script>
